import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchReferralOffer } from "../services/referralsService";
import type { Coupon, SubscriptionPlan } from "../types/billing.types";
import type { ReferralPartner } from "../types/referrals.types";

/**
 * `unavailable` is kept and no longer reachable.
 *
 * It meant "there is no endpoint yet, so nothing was asked" — the state the
 * dialog sat in while `/billing/coupons/` was agreed and unbuilt. The lookup
 * exists now and is always asked, so a list with nothing in it is `ready` and
 * empty, which the dialog already draws the same way. The value stays because
 * the dialog still branches on it and removing it would be a change to a
 * screen for no gain.
 */
export type CouponsStatus = "unavailable" | "loading" | "ready" | "failed";

export interface CouponsState {
  coupons: Coupon[];
  /**
   * The partner behind the referral, where the practice came through a link
   * and their coupon is live. `null` for everybody else, which is most
   * practices. Nothing draws it yet; it is what a row would name if the dialog
   * grew a line saying whose offer it is.
   */
  partner: ReferralPartner | null;
  status: CouponsStatus;
  error?: string;
  reload: () => void;
}

const EMPTY: Coupon[] = [];

/**
 * The coupons on offer, for the dialog behind "View coupons".
 *
 * `GET /api/v1/referrals/`, asked with the practice's `referral_code` where
 * they came through a partner's link and with nothing at all where they did
 * not. The partner's own coupon leads the list where there is a usable one;
 * Naurist's follow. See `fetchReferralOffer`.
 *
 * Fetched only when the dialog is actually opened — `enabled` — because a list
 * nobody has asked to see is a request on every checkout for a panel most
 * buyers never open. That matters more here than it used to: the lookup is
 * public and throttled to 30 a minute per IP, so asking for it unprompted
 * would spend somebody else's budget. It is kept once fetched, so opening the
 * dialog a second time is instant.
 *
 * **Which terms a coupon holds for is read where the row says.** A row naming
 * `applicable_plan_codes` has them resolved against `plans` — the price list
 * the screen has already loaded, which is the only thing that can say which
 * term a plan code is. A row naming none falls back to what is known about its
 * code and otherwise comes through with no terms, which the dialog reads as
 * "holds everywhere". None of this decides anything: the quote is what refuses
 * a code that does not hold, and it refuses it with the reason to print. Here
 * it only decides whether a row offers "Apply" or "Switch plan".
 *
 * Must be used inside a Clerk provider. The reference calls this route public,
 * and the deployed API answers `401` without a bearer token, so the session
 * goes up with it — see `fetchReferralOffer`.
 */
export function useCoupons(
  enabled: boolean,
  referralCode?: string | null,
  plans: SubscriptionPlan[] = []
): CouponsState {
  const getToken = useTokenGetter();

  /**
   * The price list, read at the moment a row is resolved rather than watched.
   *
   * Through a ref because the resolver below goes into the fetch's
   * dependencies: a plain closure over `plans` would be a new function on
   * every render of the screen, and the list would be fetched again each time
   * the checkout re-rendered — on every keystroke in the coupon field, among
   * other things, against an endpoint throttled to 30 a minute. The ref makes
   * the resolver stable while still reading whatever the price list is when a
   * row actually needs resolving, which is after the response lands.
   */
  const plansRef = useRef(plans);
  plansRef.current = plans;

  const cycleOfCode = useCallback(
    (code: string) =>
      plansRef.current.find((plan) => plan.code === code)?.id ?? null,
    []
  );

  const [coupons, setCoupons] = useState<Coupon[]>(EMPTY);
  const [partner, setPartner] = useState<ReferralPartner | null>(null);
  const [status, setStatus] = useState<CouponsStatus>("loading");
  const [error, setError] = useState<string | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();
    setStatus("loading");

    fetchReferralOffer(getToken, referralCode, controller.signal, cycleOfCode)
      .then((offer) => {
        if (controller.signal.aborted) return;
        setCoupons(offer.coupons);
        setPartner(offer.partner);
        setError(undefined);
        setStatus("ready");
      })
      .catch((cause) => {
        if (controller.signal.aborted) return;
        setStatus("failed");
        setError(
          cause instanceof ApiError
            ? cause.message
            : "We could not reach the server. Check your connection and try again."
        );
        console.error("coupons failed to load:", cause);
      });

    return () => controller.abort();
  }, [getToken, enabled, referralCode, attempt, cycleOfCode]);

  const reload = useCallback(() => setAttempt((current) => current + 1), []);

  return { coupons, partner, status, error, reload };
}

export default useCoupons;
